// Decides whether an admin questionnaire review row should render the FROZEN
// "originally approved" state for a compliance document the vendor later
// replaced (renewal), instead of the live submission state.
//
// Background (PR #157): when a vendor replaces/removes an approved document,
// the old submission_files rows get superseded_at + superseded_status
// ='approved' and the submission is demoted to status='pending'
// (+ reverify_requested_at). The whole renewal review — new upload, verdict,
// vendor notification — happens in the admin Compliance Documents tab. The
// questionnaire row is a permanent record of the original approved answer:
// it shows the originally-approved file(s) with an "Approved" badge and never
// updates, not even after the replacement is itself approved.
//
// The admin submissions endpoint (GET /api/companies/:id/submissions) attaches
// `approved_files`: the superseded_status='approved' files with the MINIMUM
// superseded_at for the submission — the first approved version that got
// superseded (see apps/api/src/lib/originalApprovedFiles.ts). Empty for
// submissions never replaced-after-approval.
//
// Gated on the question being a COMPLIANCE document (`include_in_compliance_
// documents`): the renewal review lives in the Compliance Documents tab, which
// only mirrors compliance-flagged questions, so freezing a non-compliance doc
// would strand it with no review surface anywhere. Vendors can only replace an
// already-approved document when it is a compliance doc (reviewLock.ts), so
// this gate loses no vendor-driven case — it only excludes admin replacements
// of non-compliance answers.
//
// Kept React-free and structural (generic over the file shape) so it is
// unit-testable next to source.

export type FrozenApprovedDocState<F> =
  | { frozen: false }
  | { frozen: true; files: F[]; status: 'approved' };

export function frozenApprovedDoc<F>(
  submission:
    | { approved_files?: F[] | null; status?: string; reverify_requested_at?: string | null }
    | undefined,
  isComplianceDoc: boolean,
): FrozenApprovedDocState<F> {
  const files = submission?.approved_files ?? [];
  if (!isComplianceDoc || files.length === 0) return { frozen: false };
  // Freeze only while the renewal is in-flight (reverify pending) or was
  // re-approved. A terminal N/A verdict clears reverify_requested_at and leaves
  // the doc OUT of the Compliance Documents tab (which needs approved/reverify),
  // so freezing an N/A'd renewal would strand it with no review surface — show
  // the live state instead so the admin can still act on it.
  const renewalPendingOrApproved =
    submission?.status === 'approved' || submission?.reverify_requested_at != null;
  if (!renewalPendingOrApproved) return { frozen: false };
  return { frozen: true, files, status: 'approved' };
}
